import { Button, ButtonBase, IconButton, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import CheckInDialogComponent from 'src/clinic/components/CheckIn'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import { ClinicItem } from '../MentorClinicBookingStyles'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import LocalHospital from '@mui/icons-material/LocalHospital'
import PeopleIcon from '@mui/icons-material/People'
import LocationOn from '@mui/icons-material/LocationOn'
import Refresh from '@mui/icons-material/Refresh'
import { peekIntoRoom } from 'src/clinic/components/CheckIn/services'
import { getEventTypesFromRoomData } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import { getTeacherChamberRoomScreensStatus } from 'src/consumer/services/featureServices'
import ChamberRoomStatusIndicator from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/ChamberRoomStatusIndicator'

/** Derive room status from API list: 'online' if any screen is up, else 'offline', or 'unknown' if not in list */
function getRoomStatusFromScreenStatuses(chamberRoomId, screenStatuses) {
  if (!chamberRoomId || !Array.isArray(screenStatuses) || screenStatuses.length === 0) return 'unknown'
  const roomScreens = screenStatuses.filter((s) => s.chamberRoomId === chamberRoomId)
  if (roomScreens.length === 0) return 'unknown'
  const hasOnline = roomScreens.some((s) => s.status === 'up')
  return hasOnline ? 'online' : 'offline'
}
import { Box } from '@mui/material'

export default function MentorAssociationClinicSelectionBox({
  clinicLocationData,
  showJoinHotline,
  showCheckInPerson,
  hotlineOnClick,
  showViewStatsButton,
  onClickViewStatsButton,
  hideClinicRelatedInfo = false,
  isolatedChamberRoomId,
  containerCustomStyles = {},
  onChoosingInPerson,
  onChoosingRemote,
  onChoosingPeekIn,
  hideEmrPatientsButtons = false,
  hidePatientListingButton = false,
  checkedInDetails = null,
  screenStatusesList: screenStatusesListProp,
  onRefreshChamberStatuses,
}) {
  const navigate = useNavigate()
  const currentDay = dayjs().format('dddd').toLowerCase()
  const { mentorProfileData } = useContext(MentorProfileContext)

  const [chamberRoomsOfThisLocation, setChamberRoomsOfThisLocation] = useState([])
  const [chamberRoomsOfThisLocationLoading, setChamberRoomsOfThisLocationLoading] = useState(false)
  const [auditData, setAuditData] = useState([])
  const [localScreenStatusesList, setLocalScreenStatusesList] = useState([])
  const screenStatusesList = screenStatusesListProp ?? localScreenStatusesList

  const handleRefreshStatuses = useCallback(async () => {
    const list = await getTeacherChamberRoomScreensStatus()
    setLocalScreenStatusesList(list)
  }, [])

  const onRefreshStatus = onRefreshChamberStatuses ?? handleRefreshStatuses

  const roomsToShow = useMemo(() => {
    return chamberRoomsOfThisLocation?.['defined-room-access']
      ?.filter((item) => {
        if (isolatedChamberRoomId) {
          return item?.id === isolatedChamberRoomId
        }
        return true
      })
      ?.map((item) => {
        return {
          ...item,
          occupied: item?.occupied,
        }
      })
  }, [chamberRoomsOfThisLocation, isolatedChamberRoomId, mentorProfileData?.id])

  async function getChamberRoomsForThisClinicLocation(clinicLocationId) {
    setChamberRoomsOfThisLocationLoading(true)
    const responseOne = await getMPDataWithParams(`/api/secure/teacher/associated-chamber-rooms`, {
      clinicLocationId: clinicLocationId,
    })
    setChamberRoomsOfThisLocationLoading(false)
    if (responseOne?.status === 200 && isCommonResponseSuccessful(responseOne?.data?.code)) {
      ;(responseOne?.data?.data?.['defined-room-access'] || []).forEach((item) => {
        const itemInStorage = localStorage.getItem('checkedInDetails')
        if (itemInStorage) {
          const checkedInDetails = JSON.parse(itemInStorage)
          if (checkedInDetails?.chamberRoomId === item?.id) {
            if (!(item?.occupied && item?.checkedInMentorId === mentorProfileData?.id)) {
              localStorage.removeItem('checkedInDetails')
              localStorage.removeItem('roomDataEventTypes')
            }
          }
        }
      })
      setChamberRoomsOfThisLocation(responseOne?.data?.data)
    } else {
      toast.error('Error getting allowed chamber rooms')
    }
  }

  async function getVideoAuditableRoomsForThisClinicLocation(clinicLocationId) {
    setAuditData()
    const response = await getMPDataWithParams('/api/secure/teacher/audit/chamber-room/list', {
      clinicLocationId: clinicLocationId,
    })
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      setAuditData(response?.data?.data)
    }
  }

  const clinicStatus = useMemo(() => {
    const scheduleTimezone = clinicLocationData?.schedules?.[0]?.timezone
      ? decodeURIComponent(clinicLocationData?.schedules?.[0]?.timezone)
      : '+05:30'

    const weeklyHourMap = clinicLocationData?.schedules?.[0]?.weeklyHourMap
    if (!weeklyHourMap) {
      return { status: 'unknown', message: 'Schedule unavailable', color: 'rgba(100, 116, 139, 0.9)' }
    }

    const todayTimings = weeklyHourMap?.[currentDay]

    if (!todayTimings || !todayTimings?.available) {
      return { status: 'closed', message: 'Closed today', color: 'rgba(239, 68, 68, 0.9)' }
    }

    const slotList = todayTimings?.slotList
    if (!slotList || slotList.length === 0) {
      return { status: 'closed', message: 'Closed today', color: 'rgba(239, 68, 68, 0.9)' }
    }

    const now = dayjs().utcOffset(scheduleTimezone)
    let isOpen = false
    let nextOpening = null
    let closingTime = null
    let minutesUntilClose = null

    // Check all slots for today
    for (const slot of slotList) {
      // Parse the time string (HH:mm:ss format)
      const [hours, minutes] = slot?.startTime?.split(':') || [0, 0]
      const [endHours, endMinutes] = slot?.endTime?.split(':') || [0, 0]
      
      // Set the date to today in the clinic's timezone
      const todayStart = now.startOf('day')
      const slotStart = todayStart.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)
      const slotEnd = todayStart.hour(parseInt(endHours)).minute(parseInt(endMinutes)).second(0)

      // Check if we're currently in this slot
      if (now.isAfter(slotStart) && now.isBefore(slotEnd)) {
        isOpen = true
        closingTime = slotEnd
        minutesUntilClose = slotEnd.diff(now, 'minute')
        break
      }

      // Track next opening if we haven't found one yet
      if (!nextOpening && slotStart.isAfter(now)) {
        nextOpening = slotStart
      }
    }

    if (isOpen) {
      // Check if closing soon (within 30 minutes)
      if (minutesUntilClose <= 30) {
        return {
          status: 'closing-soon',
          message: `Closes in ${minutesUntilClose} min`,
          color: 'rgba(245, 158, 11, 0.9)',
          closingTime: closingTime.format('h:mm A'),
        }
      }
      return {
        status: 'open',
        message: 'Open',
        color: 'rgba(16, 185, 129, 0.9)',
        closingTime: closingTime.format('h:mm A'),
      }
    } else if (nextOpening) {
      const minutesUntilOpen = nextOpening.diff(now, 'minute')
      if (minutesUntilOpen <= 60) {
        return {
          status: 'opening-soon',
          message: `Opens in ${minutesUntilOpen} min`,
          color: 'rgba(59, 130, 246, 0.9)',
          openingTime: nextOpening.format('h:mm A'),
        }
      }
      return {
        status: 'closed',
        message: `Opens at ${nextOpening.format('h:mm A')}`,
        color: 'rgba(100, 116, 139, 0.9)',
      }
    }

    // All slots have passed, check if there are more slots today or closed
    return { status: 'closed', message: 'Closed for today', color: 'rgba(239, 68, 68, 0.9)' }
  }, [clinicLocationData, currentDay])

  useEffect(() => {
    const fetchChamberRooms = () => {
      getChamberRoomsForThisClinicLocation(clinicLocationData?.id)
      getVideoAuditableRoomsForThisClinicLocation(clinicLocationData?.id)
    }
    fetchChamberRooms()
    const intervalId = setInterval(fetchChamberRooms, 120000)
    return () => clearInterval(intervalId)
  }, [clinicLocationData?.id])

  // Local fetch only when parent does not provide screenStatusesList (API returns all rooms in one response)
  useEffect(() => {
    if (screenStatusesListProp != null || !clinicLocationData?.id || !roomsToShow?.length) return
    const controller = new AbortController()
    const fetchStatuses = async () => {
      const list = await getTeacherChamberRoomScreensStatus(controller.signal)
      if (!controller.signal.aborted) setLocalScreenStatusesList(list)
    }
    fetchStatuses()
    const statusInterval = setInterval(fetchStatuses, 30000)
    return () => {
      clearInterval(statusInterval)
      controller.abort()
    }
  }, [screenStatusesListProp, clinicLocationData?.id, roomsToShow?.length])

  // Add pulse animation style
  useEffect(() => {
    const styleId = 'clinic-status-pulse-animation'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        @keyframes status-dot-pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.2);
          }
        }
        .status-dot-pulse {
          animation: status-dot-pulse 2s ease-in-out infinite;
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  return (
    <ClinicItem style={{ cursor: 'default', ...containerCustomStyles }}>
      {!hideClinicRelatedInfo && (
        <>
          <div 
            className="d-flex flex-column gap-1" 
            style={{ 
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: 'rgba(15, 23, 42, 0.95)',
                lineHeight: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                marginBottom: '6px',
              }}
            >
              <LocationOn sx={{ fontSize: '18px', color: 'rgba(116, 80, 170, 0.8)' }} />
              <span>{clinicLocationData?.name}</span>
            </div>
            {/* Clinic Status Indicator */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: clinicStatus.color,
                  backgroundColor: `${clinicStatus.color}15`,
                  border: `1px solid ${clinicStatus.color}30`,
                }}
              >
                <div
                  className={clinicStatus.status === 'open' ? 'status-dot-pulse' : ''}
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: clinicStatus.color,
                    flexShrink: 0,
                  }}
                />
                <span>{clinicStatus.message}</span>
                {clinicStatus.closingTime && (
                  <span style={{ fontSize: '11px', opacity: 0.8, marginLeft: '2px' }}>• {clinicStatus.closingTime}</span>
                )}
                {clinicStatus.openingTime && (
                  <span style={{ fontSize: '11px', opacity: 0.8, marginLeft: '2px' }}>• {clinicStatus.openingTime}</span>
                )}
              </div>
            </div>
          </div>
          {hideEmrPatientsButtons && hidePatientListingButton && (
            <div style={{ cursor: 'pointer', marginBottom: '8px' }}>
              {hideEmrPatientsButtons && (
                <LocalHospital
                  onClick={() =>
                    navigate(
                      `${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}/emr-patients`,
                    )
                  }
                />
              )}
              {hidePatientListingButton && (
                <PeopleIcon
                  onClick={() =>
                    navigate(
                      `${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}/patient-list`,
                    )
                  }
                />
              )}
            </div>
          )}
          {showViewStatsButton && (
            <div
              onClick={() => onClickViewStatsButton(clinicLocationData)}
              style={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'rgba(116, 80, 170, 0.9)',
                fontWeight: 600,
                fontSize: '12px',
                padding: '6px 10px',
                borderRadius: '6px',
                marginTop: '6px',
                transition: 'all 0.2s ease',
                backgroundColor: 'rgba(116, 80, 170, 0.05)',
                border: '1px solid rgba(116, 80, 170, 0.15)',
                textAlign: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(116, 80, 170, 0.1)'
                e.currentTarget.style.borderColor = 'rgba(116, 80, 170, 0.25)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(116, 80, 170, 0.05)'
                e.currentTarget.style.borderColor = 'rgba(116, 80, 170, 0.15)'
              }}
            >
              View stats
            </div>
          )}
        </>
      )}
      <div style={{ width: '100%' }}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'right',
            height: '24px',
            marginBottom: '8px',
          }}
        >
          {chamberRoomsOfThisLocationLoading && (
            <Spinner animation="border" variant="dark" size="sm" />
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
          {roomsToShow?.map((item) => {
            // Check if this room is currently checked in
            const isCheckedInRoom =
              checkedInDetails?.chamberRoomId === item?.id &&
              checkedInDetails?.clinicLocationId === clinicLocationData?.id

            return (
              <ChamberRoomInsideChamberLocation
                key={item?.id}
                roomData={item}
                showJoinHotline={showJoinHotline}
                clinicLocationData={clinicLocationData}
                hotlineOnClick={hotlineOnClick}
                auditData={auditData}
                onChoosingInPerson={onChoosingInPerson}
                onChoosingRemote={onChoosingRemote}
                onChoosingPeekIn={onChoosingPeekIn}
                isCheckedInRoom={isCheckedInRoom}
                roomScreenStatus={getRoomStatusFromScreenStatuses(item?.id, screenStatusesList)}
                onRefreshStatus={onRefreshStatus}
              />
            )
          })}
          {roomsToShow?.length === 0 && roomsToShow?.length === 0 && (
            <div>
              <i>No rooms are set up for you in this location</i>
            </div>
          )}
        </div>
      </div>

      {/* 
        /
        /
        /
        /
        /
        /
        /
      */}

      {/* legacy */}
      {/* <>
        <div style={{ width: '100%', height: '2px', background: '#fff' }}></div>
        <div
          style={{
            cursor: 'pointer',
            padding: '5px 0',
            borderRadius: '8px',
            background: '#cbd3ff',
            border: '1px solid #a2a8cc',
          }}
          onClick={() => navigate(clinicLocationData?.id)}
        >
          Continue w/o Checkin
        </div>
      </> */}
    </ClinicItem>
  )
}
const ChamberRoomInsideChamberLocation = ({
  roomData,
  clinicLocationData,
  showJoinHotline,
  hotlineOnClick,
  auditData,
  onChoosingInPerson,
  onChoosingRemote,
  onChoosingPeekIn,
  isCheckedInRoom = false,
  roomScreenStatus: roomScreenStatusProp = 'unknown',
  onRefreshStatus,
}) => {
  const navigate = useNavigate()
  const [checkInModalOpen, setCheckInModalOpen] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [vacateVisible, setVacateVisible] = useState(true)
  const roomScreenStatus = roomScreenStatusProp

  const handleRefresh = async () => {
    if (!onRefreshStatus) return
    setRefreshing(true)
    try {
      await onRefreshStatus()
    } finally {
      setRefreshing(false)
    }
  }

  const roomOccupied = roomData?.occupied
  const occupiedByThisMentor =
    roomData?.occupied && roomData?.checkedInMentorId === mentorProfileData?.id
  const checkedInMentorName = roomData?.checkedInMentorName

  async function sendVacateRequest(roomData) {
    toast.success('vacate request sent successfully')
    setVacateVisible(false)
  }

  async function informPeekIn() {
    const response = await peekIntoRoom({
      ...roomData,
      logEventId: 'enter-without-checkin',
    })
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
    } else {
      // Silently handle check-in inform failure
    }
  }

  return (
    <div>
      <div
        style={{
          background: isCheckedInRoom
            ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)'
            : '#fff',
          borderRadius: '8px',
          border: isCheckedInRoom
            ? '2px solid rgba(116, 80, 170, 0.5)'
            : '1px solid #cecece',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: '90px',
          width: '175px',
          boxShadow: isCheckedInRoom
            ? '0 6px 20px rgba(116, 80, 170, 0.25)'
            : 'none',
          transform: isCheckedInRoom ? 'scale(1.05)' : 'scale(1)',
          transition: 'all 0.3s ease',
          position: 'relative',
        }}
      >
        {isCheckedInRoom && (
          <div
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              backgroundColor: 'rgba(116, 80, 170, 0.95)',
              color: 'white',
              fontSize: '9px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '4px',
              zIndex: 10,
              boxShadow: '0 2px 8px rgba(116, 80, 170, 0.4)',
            }}
          >
            CHECKED IN
          </div>
        )}
        <div
          style={{
            paddingRight: isCheckedInRoom ? '70px' : '0',
            paddingTop: '4px',
            fontWeight: 600,
            fontSize: '14px',
            color: 'rgba(15, 23, 42, 0.9)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {roomData?.title}
          </span>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0 }}>
            <ChamberRoomStatusIndicator
              status={roomScreenStatus}
              chamberRoomName={roomData?.title}
              compact={true}
            />
            <Tooltip title="Refresh status">
              <span>
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  aria-label="Refresh chamber room status"
                  sx={{
                    padding: '2px',
                    color: 'rgba(116, 80, 170, 0.8)',
                    '&:hover': { color: 'rgba(116, 80, 170, 1)', backgroundColor: 'rgba(116, 80, 170, 0.08)' },
                    '&.Mui-disabled': { color: 'rgba(116, 80, 170, 0.4)' },
                  }}
                >
                  {refreshing ? (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: '2px solid rgba(116, 80, 170, 0.3)',
                        borderTopColor: 'rgba(116, 80, 170, 0.9)',
                        animation: 'spin 0.8s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  ) : (
                    <Refresh sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </div>
        <div>
          {checkedInMentorName && (
            <div style={{ backgroundColor: '#e5e8eb', fontSize: '12px' }}>
              Present: {checkedInMentorName}
            </div>
          )}
          <div
            style={{
              borderRadius: '0 0 4px 4px',
              backgroundColor: roomOccupied ? '#D43A70ed' : '#71A754',
              color: '#fff',
            }}
          >
            {roomOccupied
              ? occupiedByThisMentor
                ? 'YOU ARE CHECKED IN'
                : 'ROOM BUSY'
              : 'ROOM AVAILABLE'}
          </div>
        </div>
        {checkInModalOpen && (
          <CheckInDialogComponent
            closeFunction={() => {
              setCheckInModalOpen(false)
            }}
            roomData={roomData}
            clinicLocationData={clinicLocationData}
            onChoosingInPerson={() => {
              if (onChoosingInPerson) {
                onChoosingInPerson(clinicLocationData, roomData)
              } else {
                navigate(`${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`)
              }
            }}
            onChoosingRemote={() => {
              if (onChoosingRemote) {
                onChoosingRemote(clinicLocationData, roomData)
              } else {
                navigate(
                  `/mentor-home/join-chamber-room/${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`,
                )
              }
            }}
            showJoinHotline={showJoinHotline}
            hotlineOnClick={hotlineOnClick}
          />
        )}
      </div>
      <div style={{ height: '130px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {(!Boolean(roomOccupied) || Boolean(occupiedByThisMentor)) && (
          <Button
            color="emrSecondary"
            sx={{
              marginTop: '4px',
              borderRadius: '16px',
              padding: '4px 8px',
              width: '175px',
              textTransform: 'unset',
            }}
            size="large"
            variant="contained"
            onClick={() => {
              if (roomOccupied && !Boolean(occupiedByThisMentor)) {
              } else {
                setCheckInModalOpen(true)
              }
            }}
          >
            {Boolean(occupiedByThisMentor) ? 'Proceed' : 'Check In'}
          </Button>
        )}
        {Boolean(
          (auditData?.['chamber-room-channel'] || [])?.find(
            (item) => item?.chamberRoomId === roomData?.id,
          ),
        ) && (
          <Button
            color="emrSecondary"
            sx={{
              marginTop: '4px',
              borderRadius: '16px',
              padding: '4px 8px',
              width: '175px',
              textTransform: 'unset',
            }}
            size="large"
            variant="contained"
            onClick={() => {
              const currentChannel = (auditData?.['chamber-room-channel'] || [])?.find(
                (item) => item?.chamberRoomId === roomData?.id,
              )
              localStorage.setItem('auditRoomDetails', JSON.stringify(roomData))
              navigate(
                `/mentor-home/join-as-auditor/${roomData?.id}/${currentChannel?.id}`,
              )
            }}
          >
            Video Audit
          </Button>
        )}
        {!Boolean(occupiedByThisMentor) && (
          <Button
            color="emrSecondary"
            sx={{
              marginTop: '4px',
              borderRadius: '16px',
              padding: '4px 8px',
              width: '175px',
              textTransform: 'unset',
            }}
            size="large"
            variant="outlined"
            onClick={() => {
              if (onChoosingPeekIn) {
                onChoosingPeekIn(clinicLocationData, roomData)
              } else {
                navigate(`${clinicLocationData?.clinicBrandId}/${clinicLocationData?.id}`)
              }
              informPeekIn()
              localStorage.setItem('peekedInRoomDetails', JSON.stringify(roomData))
              
              // Extract and store eventTypes from roomData
              if (roomData?.checkInEventOptions) {
                const eventTypes = getEventTypesFromRoomData(
                  roomData.checkInEventOptions,
                  [], // mentorEventTypes not available here, will be matched later
                  roomData?.clinicLocationId
                )
                if (eventTypes.length > 0) {
                  localStorage.setItem('roomDataEventTypes', JSON.stringify(eventTypes))
                }
              }
            }}
          >
            Peek in / Video calls
          </Button>
        )}
        {Boolean(occupiedByThisMentor) && (
          <CheckoutButtonComponent styleProp={{ padding: '4px' }} buttonColor="error" />
        )}
        {/* {Boolean(roomOccupied) && !Boolean(occupiedByThisMentor) && vacateVisible && (
          <ButtonBase
            sx={{
              marginTop: '4px',
              background: '#fadb2f',
              color: '#000',
              borderRadius: '8px',
              padding: '4px 8px',
              boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
              width: '175px',
              fontWeight: 300,
            }}
            onClick={() => {
              sendVacateRequest(roomData)
            }}
          >
            <ForwardToInboxOutlinedIcon style={{ fontSize: '16px' }} />
            <span style={{ marginLeft: '8px' }}>Request to Vacate</span>
          </ButtonBase>
        )} */}
      </div>
    </div>
  )
}
